<script>
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { validatePassword, getStrengthColor, getStrengthLabel } from '../utils/passwordValidation';

  let code = '';
  let email = '';
  let newPassword = '';
  let confirmPassword = '';
  let showPassword = false;
  let showConfirmPassword = false;
  let loading = false;
  let error = '';
  let success = false;

  // Password validation state
  let passwordValidation = { valid: false, errors: [], strength: 'none', checks: {} };
  let passwordsMatch = true;

  // Reactive validation
  $: {
    if (newPassword) {
      passwordValidation = validatePassword(newPassword);
    } else {
      passwordValidation = { valid: false, errors: [], strength: 'none', checks: {} };
    }
  }

  $: {
    if (confirmPassword) {
      passwordsMatch = newPassword === confirmPassword;
    } else {
      passwordsMatch = true;
    }
  }

  $: formValid = passwordValidation.valid && passwordsMatch && confirmPassword.trim() !== '';

  onMount(() => {
    // Get query parameters from URL
    const urlParams = new URLSearchParams(window.location.search);
    code = urlParams.get('code') || '';
    // Normalize email to lowercase for case-insensitive matching
    email = (urlParams.get('email') || '').toLowerCase().trim();

    if (!code || !email) {
      error = 'Invalid password reset link. Please request a new password reset.';
    }
  });

  function togglePasswordVisibility() {
    showPassword = !showPassword;
  }

  function toggleConfirmPasswordVisibility() {
    showConfirmPassword = !showConfirmPassword;
  }

  async function handleSubmit() {
    if (!formValid) {
      error = 'Please fix the errors below';
      return;
    }

    loading = true;
    error = '';

    try {
      const response = await fetch('/api/password-reset/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, newPassword })
      });

      const data = await response.json();

      if (data.success) {
        success = true;
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        error = data.error || 'Failed to reset password. Please try again.';
        if (data.details && Array.isArray(data.details)) {
          error += '\n' + data.details.join('\n');
        }
        if (data.expired) {
          error += '\n\nPlease request a new password reset link.';
        }
      }
    } catch (err) {
      console.error('Password reset error:', err);
      error = 'An error occurred. Please try again.';
    } finally {
      loading = false;
    }
  }
</script>

<div class="min-h-screen flex items-center justify-center bg-gray-100">
  <div class="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow-md">
    <div class="text-center">
      <img class="w-3/4 mx-auto h-24 w-auto rounded-md" src="/images/buildzilla-logo-lg.jpg" alt="Logo">
      <h2 class="mt-6 text-xl font-medium text-gray-900">Set New Password</h2>
    </div>

    {#if success}
      <!-- Success Message -->
      <div class="mt-8 space-y-6">
        <div class="p-4 bg-green-50 border border-green-200 rounded-md">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-6 w-6 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-green-800">Password Changed Successfully!</h3>
              <div class="mt-2 text-sm text-green-700">
                <p>Your password has been reset. You can now log in with your new password.</p>
                <p class="mt-2">Redirecting to login page...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    {:else if !code || !email}
      <!-- Invalid Link Error -->
      <div class="mt-8 space-y-6">
        <div class="p-4 bg-red-50 border border-red-200 rounded-md">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-6 w-6 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800">Invalid Link</h3>
              <div class="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="text-center space-y-3">
          <button
            on:click={() => navigate('/request-password-reset')}
            class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#313b55] hover:bg-[rgb(158,158,158)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(178,178,178)]"
          >
            Request New Reset Link
          </button>
          <button
            on:click={() => navigate('/login')}
            class="text-sm text-[#313b55] hover:text-[rgb(158,158,158)] font-medium"
          >
            ← Back to Login
          </button>
        </div>
      </div>
    {:else}
      <!-- Password Reset Form -->
      <form on:submit|preventDefault={handleSubmit} class="mt-8 space-y-6">
        <p class="text-sm text-gray-600">
          Enter your new password below.
        </p>

        <!-- New Password Field -->
        <div>
          <label for="newPassword" class="block text-sm font-medium text-gray-700">New Password</label>
          <div class="relative mt-1">
            {#if showPassword}
              <input 
                id="newPassword" 
                name="newPassword" 
                type="text"
                required 
                bind:value={newPassword}
                class="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[rgb(178,178,178)] focus:border-[rgb(178,178,178)]"
                placeholder="Enter new password"
                disabled={loading}
              >
            {:else}
              <input 
                id="newPassword" 
                name="newPassword" 
                type="password"
                required 
                bind:value={newPassword}
                class="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[rgb(178,178,178)] focus:border-[rgb(178,178,178)]"
                placeholder="Enter new password"
                disabled={loading}
              >
            {/if}
            <button
              type="button"
              on:click={togglePasswordVisibility}
              class="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
              tabindex="-1"
            >
              {showPassword ? '🙈' : '👁️'}
            </button>
          </div>

          <!-- Password Strength Indicator -->
          {#if newPassword}
            <div class="mt-2">
              <div class="flex items-center space-x-2">
                <div class="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    class="h-full transition-all duration-300 {getStrengthColor(passwordValidation.strength)}"
                    style="width: {passwordValidation.strength === 'strong' ? '100%' : passwordValidation.strength === 'good' ? '75%' : passwordValidation.strength === 'fair' ? '50%' : passwordValidation.strength === 'weak' ? '33%' : '20%'}"
                  ></div>
                </div>
                <span class="text-xs font-medium text-gray-600">
                  {getStrengthLabel(passwordValidation.strength)}
                </span>
              </div>
            </div>
          {/if}

          <!-- Password Requirements -->
          <div class="mt-3 space-y-1 text-sm">
            <p class="text-gray-600 font-medium">Password must contain:</p>
            <div class="flex items-center space-x-2 {passwordValidation.checks.length ? 'text-green-600' : 'text-gray-500'}">
              <span>{passwordValidation.checks.length ? '✓' : '○'}</span>
              <span>At least 8 characters</span>
            </div>
            <div class="flex items-center space-x-2 {passwordValidation.checks.uppercase ? 'text-green-600' : 'text-gray-500'}">
              <span>{passwordValidation.checks.uppercase ? '✓' : '○'}</span>
              <span>One uppercase letter</span>
            </div>
            <div class="flex items-center space-x-2 {passwordValidation.checks.special ? 'text-green-600' : 'text-gray-500'}">
              <span>{passwordValidation.checks.special ? '✓' : '○'}</span>
              <span>One special character (!@#$%^&*...)</span>
            </div>
          </div>
        </div>

        <!-- Confirm Password Field -->
        <div>
          <label for="confirmPassword" class="block text-sm font-medium text-gray-700">Confirm New Password</label>
          <div class="relative mt-1">
            {#if showConfirmPassword}
              <input 
                id="confirmPassword" 
                name="confirmPassword" 
                type="text"
                required 
                bind:value={confirmPassword}
                class="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[rgb(178,178,178)] focus:border-[rgb(178,178,178)] {confirmPassword && !passwordsMatch ? 'border-red-500' : ''}"
                placeholder="Confirm new password"
                disabled={loading}
              >
            {:else}
              <input 
                id="confirmPassword" 
                name="confirmPassword" 
                type="password"
                required 
                bind:value={confirmPassword}
                class="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[rgb(178,178,178)] focus:border-[rgb(178,178,178)] {confirmPassword && !passwordsMatch ? 'border-red-500' : ''}"
                placeholder="Confirm new password"
                disabled={loading}
              >
            {/if}
            <button
              type="button"
              on:click={toggleConfirmPasswordVisibility}
              class="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
              tabindex="-1"
            >
              {showConfirmPassword ? '🙈' : '👁️'}
            </button>
          </div>
          {#if confirmPassword && !passwordsMatch}
            <p class="mt-1 text-sm text-red-600">Passwords do not match</p>
          {/if}
        </div>

        <!-- Error Message -->
        {#if error}
          <div class="p-3 bg-red-50 border border-red-200 rounded-md">
            <p class="text-sm text-red-800 whitespace-pre-line">{error}</p>
          </div>
        {/if}

        <!-- Submit Button -->
        <div>
          <button
            type="submit"
            disabled={!formValid || loading}
            class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#313b55] hover:bg-[rgb(158,158,158)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(178,178,178)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {#if loading}
              <span class="flex items-center">
                <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Changing Password...
              </span>
            {:else}
              Change Password
            {/if}
          </button>
        </div>

        <div class="text-center">
          <button
            type="button"
            on:click={() => navigate('/login')}
            class="text-sm text-[#313b55] hover:text-[rgb(158,158,158)] font-medium"
          >
            ← Back to Login
          </button>
        </div>

        <div class="text-center text-sm text-gray-500 mt-4">
          © 2025 All rights reserved
        </div>
      </form>
    {/if}
  </div>
</div>


